import React, { createContext, useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hooks";
import { useConsole } from "../../common/console/Console";
import { useAlert } from "../../errorhandling/UseAlert";
import { useUserWithAttributes } from "../../../services/auth/user";
import { updateUserAsync } from "../../../services/redux/authSlice";
import { fnDictGetIndex } from "../../../services/shared";
import { WebSocketCallback, deregisterCallback, registerCallback
    , selectCallbackRegistry, selectConnected, selectCurrentEventListeners, selectIsEventNotified
    , selectQueuedEventListeners
    , selectSessionID, setListenerContext 
} from "../../../services/redux/shuteSlice";
import { useConnectAndSetNotifyEventListers, useEventNotifier } from "../../../hooks/useEventNotifier";
import { callbackFunctions } from "../../../services/redux/shuteCallbackRegistry";

export type WrappedSputnikControls = string;  // Making it a basic string type
// export type ExpansionsState = Record<string, boolean|number|string>;
export type ExpansionsState = {
    expansions: {
        [key: string]: boolean | number | string;
    };
    tableFilters?: Record<string, any>;  // Add this property
    tableSorter?: Record<string, any>;   // Add this property
};

export type TabConfig = {
    label: string;
    component: React.ComponentType; // This will represent the component associated with the tab
  };
type ControlPrefsItem = Record<string,any>; //{ [key: string]: any };
type ControlPrefsType = ControlPrefsItem | ControlPrefsItem[];  
export interface WrappedSputnikProps<T extends object, U extends string> {
    startUserControlState?: Record<string, any>;
    // onShuteMessage?: (data: any) => void;
    // onShuteMessageError?: (error: any) => void;
    notifyTarget?: string;   
    notifyEventTypes?: string[];  
    isNotifyEventListenersComplete?: boolean;
    data?:any;
    error?:any;  
    // expansions?: ExpansionsState; 
    userSavedComponentPrefsKey?: string; 
    controlPrefs?: ExpansionsState;
    onUserControlChange?: (action: U, value: any) => void;  // Use U here
    onUserInputChangeEvent?: (state: Record<string, any>) => void;
    onNotifyConnected?: (status: boolean) => void;
    // setExpansions?: React.Dispatch<React.SetStateAction<ExpansionsState>>|null;
    updateControlPrefs?: (controlPrefs: ControlPrefsType) => void; 
    // updateExpansions?: (key: string, value?: boolean|number|string) => void;
}


interface EventTypeListenerInfoItem {
    eventTypes: string[];
    callbackName: string;
    psPageSubContext: string;
}
interface ProcessedListenerInfoItem {
    eventTypes: string[];
    psPageSubContext: string;
}
interface ISputnikContext {
    showAlert: (message: string, options?: any) => void;
    fnConsoleError: (message: string, ...optionalParams: any[]) => void;
    fnConsoleLog: (message: string, ...optionalParams: any[]) => void;
    setEventTypeListener: (eventTypes: string[], callback: (data: any) => void, psPageSubContext?: string, setEventCallbackNames?: any) => void;
    isEventListening: (eventName: string) => boolean;
    updateMountState: (action: 'mount' | 'unmount') => void;
    isEventCallbackInContext: (callbackName: string, eventName: string, psPageSubContext: string) => boolean;
    callbacksNotInContext: (callbackNames: string[], eventName: string, psPageSubContext: string) => string[];
    getEventTypeListenerInfoBySubContext: (psPageSubContext: string) => EventTypeListenerInfoItem[];
}

// Create the context with a default value
const SputnikContext = createContext<ISputnikContext>({
    showAlert: () => { /* implementation */ },
    fnConsoleError: () => { /* implementation */ },
    fnConsoleLog: () => { /* implementation */ },
    setEventTypeListener: () => { /* implementation */ },
    isEventListening: () => false,
    updateMountState: () => {},
    isEventCallbackInContext: () => false,
    callbacksNotInContext: () => [],
    getEventTypeListenerInfoBySubContext: () => [],
});
export function withWrappedSputnik<T extends object, U extends string>(WrappedComponent: React.ComponentType<T>) {
    return (props: T & WrappedSputnikProps<T, U>) => {
        
        const dispatch = useAppDispatch(); 
        // const pageContexts = props.notifyTarget ? useAppSelector(state => selectPageEventContexts(state, props.notifyTarget!)) : '';
        const isConnected = useAppSelector(selectConnected);
        const sSputnikSessionID = useAppSelector(selectSessionID); 
        const tCurrentEventListeners = useAppSelector(selectCurrentEventListeners);
        const tQueuedEventListeners = useAppSelector(selectQueuedEventListeners);
        const tCallbackRegistry = useAppSelector(selectCallbackRegistry);
                
        const [controlPrefs, setLocalControlPrefs] = useState<ControlPrefsType | null>(null);
        const [eventAction, setEventAction] = useState<'mount' | 'unmount'>('mount');

        const user = useUserWithAttributes(); 


        useEffect(() => {
            if (!props.userSavedComponentPrefsKey || !user?.userattributes?.state) {return}
            setLocalControlPrefs(fnDictGetIndex(user,`userattributes.state.${props.userSavedComponentPrefsKey}.last`,undefined,{expansions:{}}));
        }, [user,props.userSavedComponentPrefsKey]);


        const showAlert = useAlert(); 

        const updateMountState = (action: 'mount' | 'unmount') => {
            // console.log(`set action ${action}`)
            setEventAction(action);
        };
        const { fnConsoleError, fnConsoleLog } = useConsole();
    
        // Wrapper functions
        const fnConsoleErrorWrapper = (message: string, ...optionalParams: any[]) => {
            showAlert(message)
            fnConsoleError(dispatch, message, ...optionalParams);
        };
    
        const fnConsoleLogWrapper = (message: string, ...optionalParams: any[]) => {
            fnConsoleLog(dispatch, message, ...optionalParams);
        };
        const isNotifyEventListenersComplete = 
            props.notifyTarget && props.notifyEventTypes?.length 
                ? useConnectAndSetNotifyEventListers(
                    props.notifyTarget,
                    props.notifyEventTypes,
                    "add",
                    dispatch
                )
                : false
   
        useEffect(() => {
            console.log("Effect triggered: isNotifyEventListenersComplete", isNotifyEventListenersComplete);
            props.onNotifyConnected && props.onNotifyConnected(isNotifyEventListenersComplete);
        }, [isNotifyEventListenersComplete]);   

        const [eventTypeListenerInfo, setEventTypeListenerInfo] = useState<EventTypeListenerInfoItem[]>([]); 
        const [processedListenerInfo, setProcessedListenerInfo] = useState<ProcessedListenerInfoItem[]>([]); 
        
        const areListenersEqual = (listener1:any, listener2:any) => {
            if (!listener1.eventTypes.equals(listener2.eventTypes, true)) {
                return false;
            }
        
            // Compare psPageSubContext - consider them equal if both are undefined or equal
            return listener1.psPageSubContext === listener2.psPageSubContext;
        };
        const findUnprocessedListeners = () => {
            return eventTypeListenerInfo.filter(listener1 =>
                !processedListenerInfo.some(listener2 =>
                    areListenersEqual(listener1, listener2)
                )
            );
        };        
        useEffect(() => {
            // console.log(tCurrentEventListeners,eventTypeListenerInfo,processedListenerInfo);
        
            const unprocessedListeners = findUnprocessedListeners();
            if (!unprocessedListeners || unprocessedListeners.length === 0) {
                return;
            }
            // console.log(unprocessedListeners)
            const psPage = props.notifyTarget
            unprocessedListeners.forEach(({ eventTypes, callbackName, psPageSubContext }) => {
                // console.log(pageContexts)
				if (!(psPage && eventAction && eventTypes && eventTypes.length)) {
                    // console.log('quick exit',psPage,eventAction,eventTypes);
                    // setIsListenersSet(true);
                    return;
                }

                let tPendingEventListeners:string[] = [];

                const newEventListeners = eventTypes.filter(
                    (item) => !tCurrentEventListeners.includes(item)
                );
                // console.log(newEventListeners)
                if (eventAction === 'mount' && (!newEventListeners || newEventListeners.length === 0)) {
                    // console.log(`ptWatchEvents: ${eventTypes} already listening`, callbackName, psPageSubContext);
                    // update the context
                    eventTypes.forEach(event => {
                        // console.log('setting context',psPage,event,psPageSubContext)
                        dispatch(setListenerContext({ eventAction:'add', page: psPage, event: event, context: psPageSubContext }));
                    });  
                                  
                } else if (eventAction === 'unmount') {
                    // Unmount: remove events that ARE currently registered
                    const eventsToRemove = eventTypes.filter(
                        (item) => tCurrentEventListeners.includes(item)
                    );
                    if (eventsToRemove.length > 0) {
                        useEventNotifier({
                            psPage,
                            ptWatchEvents: eventsToRemove,
                            shouldExecute: isConnected,
                            psPageSubContext: psPageSubContext || '',
                            psAction: 'remove',
                            ptCurrentEventListeners: tCurrentEventListeners,
                            dispatch: dispatch,
                            sSputnikSessionID: sSputnikSessionID,
                            tPendingEventListeners: tPendingEventListeners,
                            tQueuedEventListeners: tQueuedEventListeners,
                            isEventNotInQueuedListeners: false,
                        });
                        eventsToRemove.forEach(event => {
                            dispatch(setListenerContext({ eventAction: 'remove', page: psPage, event: event, context: psPageSubContext || '' }));
                        });
                    }
                } else {
                    // console.log(eventTypes, newEventListeners,callbackName, psPageSubContext)
                    useEventNotifier({
                        psPage,
                        ptWatchEvents: newEventListeners,
                        shouldExecute: isConnected,
                        psPageSubContext: psPageSubContext || '',
                        psAction: 'add',
                        ptCurrentEventListeners: tCurrentEventListeners,
                        dispatch: dispatch,
                        sSputnikSessionID: sSputnikSessionID,
                        tPendingEventListeners: tPendingEventListeners,
                        tQueuedEventListeners: tQueuedEventListeners,
                        isEventNotInQueuedListeners: false,
                    });
                    //for any that have already been added, ensure context is updated
                    eventTypes.filter(
                        (item) => tCurrentEventListeners.includes(item)
                    ).forEach(event => {
                        // console.log('setting context',psPage,event,psPageSubContext)
                        dispatch(setListenerContext({ eventAction:'add', page: psPage, event: event, context: psPageSubContext || '' }));
                        }
                    );

                }
                if (eventTypes && callbackName) {
                        eventTypes.forEach((eventType) => {if (eventAction === 'unmount') {
                                dispatch(deregisterCallback({eventName:eventType, callbackName:callbackName}))
                            } else {
                                dispatch(registerCallback({eventName:eventType, callbackName:callbackName}))
                            }
                        }
                    );
                };
                setProcessedListenerInfo(prev => [...prev, { eventTypes, psPageSubContext }]);
            });

        }, [eventTypeListenerInfo]);
        function getEventTypeListenerInfoBySubContext(
            psPageSubContext: string
            ): EventTypeListenerInfoItem[] {
                return eventTypeListenerInfo.filter(item => item.psPageSubContext === psPageSubContext);
            }
        const setEventTypeListener =  (
            eventTypes: string[],
            callback:WebSocketCallback,
            psPageSubContext?: string,
            setEventCallbackNames?: React.Dispatch<React.SetStateAction<string[]>>
            ) => {
            // console.log('set it', eventTypes,psPageSubContext);
            const pageSubContext: string = psPageSubContext || '';
            const callbackName=`${props.notifyTarget}.${callback.name}`

            // Always refresh the callback reference. Consumers typically call this
            // from a useEffect that re-runs on renders; the callback they pass in
            // closes over current state, and stale closures silently swallow events.
            callbackFunctions[callbackName] = callback;

            // Function to check if a listener is already in processedListenerInfo
            const isListenerProcessed = (newListener:any) => {
                return processedListenerInfo.some(processedListener =>
                    areListenersEqual(processedListener, newListener)
                );
            };

            // Check if the listener is already processed
            if (!isListenerProcessed({ eventTypes, psPageSubContext: pageSubContext })) {
                // console.log('here',`${props.notifyTarget}.${callback.name}`)
                try {
                    // If not processed, update eventTypeListenerInfo state
                    setEventTypeListenerInfo(prevListenerInfo => [
                        ...prevListenerInfo,
                        { eventTypes, callbackName, psPageSubContext: pageSubContext }
                    ]);
                    if (setEventCallbackNames) {
                        setEventCallbackNames(prevEventCallbackNames => {
                            // Check if the item already exists to prevent duplicates
                            if (!prevEventCallbackNames.includes(callbackName)) {
                                return [...prevEventCallbackNames, callbackName]; // Add the new item to the list
                            }
                            return prevEventCallbackNames; // Return the original list if the item is already there
                            });                        
                    }
                } catch (oErr) {
                    console.error("Error setting event type listener:", oErr);
                }
            } else {
                // Logic for when the listener is already processed (if needed)
                // console.log(eventTypes,'Listener is already processed',psPageSubContext);
            }
        };        
        const isEventListening = (eventName:string) => {
            return useAppSelector(state => selectIsEventNotified(state, eventName));
        };
        const isEventCallbackInContext = (callbackName:string,eventName:string,psPageSubContext:string) => {
            const exists = eventTypeListenerInfo.some(listener => 
                listener.callbackName === callbackName &&
                listener.eventTypes.includes(eventName) &&
                listener.psPageSubContext === psPageSubContext
            );
        
            return exists;
        };
        
        const callbacksNotInContext = (callbackNames: string[], eventName: string, psPageSubContext: string) => {
            const summaries: string[] = [];

            const queryParam = eventTypeListenerInfo.filter(item => item.psPageSubContext === psPageSubContext)
            const listeningEventWithNoMatchingRegisteredCallback =  queryParam.filter(item => {
                return item.eventTypes.some(eventType => {
                    // console.log(tCallbackRegistry)
                    const knownItem = tCallbackRegistry[eventType];
                    // console.log(knownItem,item.callbackName,eventType)
                    return !(knownItem && knownItem.callbackKeys.includes(item.callbackName));
                });
            });

            // console.log('Dont have match',ListeningEventWithNoMatchingRegisteredCallback)
            // console.log(callbackNames,eventName,psPageSubContext)
            const notAssociatedCallbacks = callbackNames.filter(callbackName => 
                !eventTypeListenerInfo.some(listener => 
                    listener.callbackName === callbackName &&
                    listener.eventTypes.includes(eventName) &&
                    listener.psPageSubContext === psPageSubContext
                )
            );
            // console.log('Dont have a callback registered',listeningEventWithNoMatchingRegisteredCallback)
            // console.log('Dont have an assocaited callback event context',notAssociatedCallbacks)
            
            listeningEventWithNoMatchingRegisteredCallback.forEach(item => {
                item.eventTypes.forEach(eventType => {
                    summaries.push(`Event (${eventType}) with callback (${item.callbackName}) is not registered`);
                });
            });

            notAssociatedCallbacks.forEach(callbackName => {
                summaries.push(`Callback (${callbackName}) on event (${eventName}) is not associated`);
            });
            // console.log(summaries)
            return summaries;
        };

        
        const updateControlPrefs = (controlPrefs: ControlPrefsType) => {
            if (typeof controlPrefs === 'function') {
                console.error("controlPrefs is a function, but it should never be!", controlPrefs);
                console.trace("ControlPrefs was set as a function at this point.");
                return;
            }            
            type ControlItem = { [key: string]: any };
            
            const dispatchUpdate = (key: string, value: any) => {
                // console.log(`updateUserAsync - setting key: userattributes.state.${props.userSavedComponentPrefsKey}.last.${key} to`,value)
                dispatch(updateUserAsync({
                    keyaddress: `userattributes.state.${props.userSavedComponentPrefsKey}.last.${key}`,
                    keyvalue: value
                }));
            };
        
            if (Array.isArray(controlPrefs)) { // If controlPrefs is an array
                controlPrefs.forEach((item: ControlItem) => {
                    const [key, value] = Object.entries(item)[0];
                    dispatchUpdate(key, value);
                });
            } else { // If controlPrefs is an object
                // console.log(controlPrefs)
                const [key, value] = Object.entries(controlPrefs)[0];
                dispatchUpdate(key, value);
            }
        };
               
    
        const additionalProps = {
            isNotifyEventListenersComplete:isNotifyEventListenersComplete,
            controlPrefs: controlPrefs,  
            updateControlPrefs: updateControlPrefs, 
        };

        return (
            <SputnikContext.Provider 
                value={{ showAlert
                    ,fnConsoleError:fnConsoleErrorWrapper
                    ,fnConsoleLog:fnConsoleLogWrapper
                    ,setEventTypeListener
                    ,isEventListening
                    ,updateMountState 
                    ,isEventCallbackInContext
                    ,callbacksNotInContext
                    ,getEventTypeListenerInfoBySubContext
                 }}>
                <WrappedComponent {...props} {...additionalProps} />
            </SputnikContext.Provider>
        );    
};
}

export const useSputnikContext = () => {
    return useContext(SputnikContext);
};